import React, { useState, useCallback } from 'react';
import { ChevronDown, CreditCard, Loader2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axiosInstance';
import countries, { getStatesForCountry } from '../../data/countries';
import toast from 'react-hot-toast';

function formatCardNumber(value) {
  const digits = value.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return digits.slice(0, 2) + ' / ' + digits.slice(2);
  return digits;
}

function detectCardBrand(number) {
  const n = number.replace(/\D/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^5[1-5]/.test(n)) return 'mastercard';
  if (/^3[47]/.test(n)) return 'amex';
  if (/^6(?:011|5)/.test(n)) return 'discover';
  if (/^35/.test(n)) return 'jcb';
  if (/^3(?:0[0-5]|[68])/.test(n)) return 'diners';
  return null;
}

const inputClass = (hasError) =>
  `w-full px-3 py-2.5 text-sm border rounded-lg transition-colors bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[var(--color-primary-500)] ${
    hasError ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-white/10'
  }`;

// `submitOverride`: when provided, the form skips its own POST to
// /payment/cards and hands the validated payload to the override
// instead. Used by the public pay-link guest checkout to route the
// same card data through /p/{slug}/charge-card with email + gateway
// metadata. Override should return `{ success: true, card?, ... }` or
// throw on failure (axios-style errors are also handled).
export default function AddCardForm({ onSuccess, submitOverride }) {
  const { t } = useTranslation('common');
  const [form, setForm] = useState({
    country: 'US', state: '', address: '', city: '', zip: '',
    fname: '', lname: '', cardnumber: '', exp_date: '', cvc: '',
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const cardBrand = detectCardBrand(form.cardnumber);
  const statesList = getStatesForCountry(form.country);

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  }, [errors]);

  const validate = () => {
    const errs = {};
    if (!form.country) errs.country = t('payment.countryRequired');
    if (!form.state.trim()) errs.state = t('payment.stateRequired');
    if (!form.address.trim()) errs.address = t('payment.addressRequired');
    if (!form.city.trim()) errs.city = t('payment.cityRequired');
    if (!form.zip.trim()) errs.zip = t('payment.zipRequired');
    if (!form.fname.trim()) errs.fname = t('payment.firstNameRequired');
    if (!form.lname.trim()) errs.lname = t('payment.lastNameRequired');
    const cardDigits = form.cardnumber.replace(/\s/g, '');
    if (cardDigits.length < 13) errs.cardnumber = t('payment.invalidCardNumber');
    const expParts = form.exp_date.split('/').map(s => s.trim());
    if (expParts.length !== 2 || !expParts[0] || !expParts[1]) errs.exp_date = t('payment.invalidExpiry');
    else if (parseInt(expParts[0], 10) < 1 || parseInt(expParts[0], 10) > 12) errs.exp_date = t('payment.invalidMonth');
    if (form.cvc.length < 3) errs.cvc = t('payment.invalidCvc');
    if (!ageConfirmed) errs.age = t('payment.ageConfirmRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setFormError('');
    try {
      const payload = {
        fname: form.fname.trim(), lname: form.lname.trim(),
        full_name: `${form.fname.trim()} ${form.lname.trim()}`,
        country: form.country, state: form.state.trim(),
        city: form.city.trim(), address: form.address.trim(), zip: form.zip.trim(),
        cardnumber: form.cardnumber, exp_date: form.exp_date,
        cvc: form.cvc, card_type: cardBrand || undefined,
      };
      // submitOverride lets callers (e.g. the pay-link guest flow)
      // redirect the same payload to a different endpoint without
      // touching the rest of this form's logic.
      const res = submitOverride
        ? await submitOverride(payload)
        : (await axiosInstance.post('/payment/cards', payload)).data;

      if (res?.success) {
        toast.success(t('payment.cardAdded'));
        if (onSuccess) onSuccess(res.card ?? res);
      } else {
        toast.error(res?.message || t('payment.failedToAddCard'));
      }
    } catch (err) {
      const data = err.response?.data;
      const debug = data?.debug;
      const ccbillResponse = debug?.ccbill_response;
      const ccbillErrors = ccbillResponse?.errors;
      let hasFieldErrors = false;
      if (ccbillErrors && Array.isArray(ccbillErrors) && ccbillErrors.length > 0) {
        const fieldMap = {
          'customerInfo.customerFname': 'fname', 'customerInfo.customerLname': 'lname',
          'customerInfo.address1': 'address', 'customerInfo.city': 'city',
          'customerInfo.state': 'state', 'customerInfo.zipcode': 'zip',
          'customerInfo.country': 'country', 'customerInfo.email': 'email',
          'paymentInfo.creditCardPaymentInfo.cardNum': 'cardnumber',
          'paymentInfo.creditCardPaymentInfo.nameOnCard': 'fname',
          'paymentInfo.creditCardPaymentInfo.expMonth': 'exp_date',
          'paymentInfo.creditCardPaymentInfo.expYear': 'exp_date',
          'paymentInfo.creditCardPaymentInfo.cvv2': 'cvc',
        };
        const fieldErrors = {};
        ccbillErrors.forEach(e => { const f = fieldMap[e.field]; if (f) fieldErrors[f] = e.message; });
        if (Object.keys(fieldErrors).length > 0) { setErrors(prev => ({ ...prev, ...fieldErrors })); hasFieldErrors = true; }
      }
      const ccbillErrorMessages = {
        '33': 'This transaction was declined. Please use a real card — test cards are not accepted.',
        '24': 'Your card was declined by the bank. Please try a different card.',
        '25': 'Your card was declined. Please check your details and try again.',
        '200000': 'Please fix the highlighted fields below and try again.',
      };
      const errorCode = ccbillResponse?.errorCode;
      const msg = (errorCode ? ccbillErrorMessages[errorCode] : null) || data?.message || t('payment.failedToAddCardRetry');
      if (!hasFieldErrors) setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCountry = countries.find(c => c.code === form.country);
  const filteredCountries = countrySearch
    ? countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
    : countries;

  const FieldError = ({ field }) => errors[field] ? <p className="text-red-400 text-[10px] mt-0.5">{errors[field]}</p> : null;

  return (
    <form onSubmit={handleSubmit} className="px-4 py-3">
      {/* Error Banner */}
      {formError && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-start gap-2">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-red-700 dark:text-red-400">{formError}</p>
        </div>
      )}

      {/* Billing Section */}
      <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{t('paymentCard.billing', 'Billing')}</p>

      <div className="grid grid-cols-2 gap-2 mb-2.5">
        {/* Country */}
        <div className="relative">
          <div
            className={`${inputClass(errors.country)} flex justify-between items-center cursor-pointer`}
            onClick={() => { setShowCountryDropdown(!showCountryDropdown); setShowStateDropdown(false); }}
          >
            <span className="text-sm truncate">{selectedCountry?.name || t('paymentCard.country', 'Country')}</span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          </div>
          {showCountryDropdown && (
            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-[#222230] border border-gray-200 dark:border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              <div className="p-1.5 sticky top-0 bg-white dark:bg-[#222230]">
                <input
                  type="text"
                  placeholder={t('paymentCard.search', 'Search...')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-white/10 rounded bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  autoFocus
                />
              </div>
              {filteredCountries.map(c => (
                <div
                  key={c.code}
                  className={`px-2.5 py-1.5 cursor-pointer text-xs hover:bg-gray-50 dark:hover:bg-white/5 ${form.country === c.code ? 'bg-[var(--color-primary-500)]/10 text-[var(--color-primary-600)] dark:text-[var(--color-primary-400)]' : 'text-gray-700 dark:text-gray-300'}`}
                  onClick={() => { handleChange('country', c.code); handleChange('state', ''); setShowCountryDropdown(false); setCountrySearch(''); }}
                >
                  {c.name}
                </div>
              ))}
            </div>
          )}
          <FieldError field="country" />
        </div>

        {/* State */}
        <div className="relative">
          {statesList ? (
            <>
              <div
                className={`${inputClass(errors.state)} flex justify-between items-center cursor-pointer`}
                onClick={() => { setShowStateDropdown(!showStateDropdown); setShowCountryDropdown(false); }}
              >
                <span className="text-sm truncate">{statesList.find(s => s.code === form.state)?.name || t('paymentCard.state', 'State')}</span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </div>
              {showStateDropdown && (
                <div className="absolute z-20 w-full mt-1 bg-white dark:bg-[#222230] border border-gray-200 dark:border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {statesList.map(s => (
                    <div
                      key={s.code}
                      className={`px-2.5 py-1.5 cursor-pointer text-xs hover:bg-gray-50 dark:hover:bg-white/5 ${form.state === s.code ? 'bg-[var(--color-primary-500)]/10 text-[var(--color-primary-600)]' : 'text-gray-700 dark:text-gray-300'}`}
                      onClick={() => { handleChange('state', s.code); setShowStateDropdown(false); }}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <input type="text" value={form.state} onChange={(e) => handleChange('state', e.target.value)} placeholder={t('paymentCard.stateProvince', 'State / Province')} className={inputClass(errors.state)} />
          )}
          <FieldError field="state" />
        </div>
      </div>

      {/* Address + City + Zip in one row */}
      <div className="mb-2.5">
        <input type="text" value={form.address} onChange={(e) => handleChange('address', e.target.value)} placeholder={t('paymentCard.address', 'Address')} className={inputClass(errors.address)} />
        <FieldError field="address" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3.5">
        <div>
          <input type="text" value={form.city} onChange={(e) => handleChange('city', e.target.value)} placeholder={t('paymentCard.city', 'City')} className={inputClass(errors.city)} />
          <FieldError field="city" />
        </div>
        <div>
          <input type="text" value={form.zip} onChange={(e) => handleChange('zip', e.target.value)} placeholder={t('paymentCard.zipPostalCode', 'Zip / Postal code')} className={inputClass(errors.zip)} />
          <FieldError field="zip" />
        </div>
      </div>

      {/* Card Section */}
      <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{t('paymentCard.cardDetails', 'Card Details')}</p>

      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div>
          <input type="text" value={form.fname} onChange={(e) => handleChange('fname', e.target.value)} placeholder={t('paymentCard.firstName', 'First name')} className={inputClass(errors.fname)} />
          <FieldError field="fname" />
        </div>
        <div>
          <input type="text" value={form.lname} onChange={(e) => handleChange('lname', e.target.value)} placeholder={t('paymentCard.lastName', 'Last name')} className={inputClass(errors.lname)} />
          <FieldError field="lname" />
        </div>
      </div>

      {/* Card number */}
      <div className="relative mb-2.5">
        <input
          type="text"
          value={form.cardnumber}
          onChange={(e) => handleChange('cardnumber', formatCardNumber(e.target.value))}
          placeholder={t('paymentCard.cardNumber', 'Card number')}
          inputMode="numeric"
          className={`${inputClass(errors.cardnumber)} pe-10`}
        />
        <div className="absolute end-2.5 top-1/2 -translate-y-1/2">
          {cardBrand ? (
            <img src={`/payicon/${cardBrand}.png`} alt={cardBrand} className="h-5 w-auto" />
          ) : (
            <CreditCard className="h-5 w-5 text-gray-300 dark:text-gray-600" />
          )}
        </div>
        <FieldError field="cardnumber" />
      </div>

      {/* Exp + CVC */}
      <div className="grid grid-cols-2 gap-2 mb-3.5">
        <div>
          <input
            type="text"
            value={form.exp_date}
            onChange={(e) => handleChange('exp_date', formatExpiry(e.target.value))}
            placeholder="MM / YY"
            inputMode="numeric"
            className={inputClass(errors.exp_date)}
          />
          <FieldError field="exp_date" />
        </div>
        <div>
          <input
            type="text"
            value={form.cvc}
            onChange={(e) => handleChange('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="CVC"
            inputMode="numeric"
            className={inputClass(errors.cvc)}
          />
          <FieldError field="cvc" />
        </div>
      </div>

      {/* Age Confirmation + Submit */}
      <label
        className={`flex items-center gap-2 mb-3.5 cursor-pointer select-none ${errors.age ? 'text-red-400' : ''}`}
        onClick={() => { setAgeConfirmed(!ageConfirmed); if (errors.age) setErrors(prev => { const n = {...prev}; delete n.age; return n; }); }}
      >
        <div className={`w-4 h-4 shrink-0 border rounded flex items-center justify-center transition-colors ${
          ageConfirmed
            ? 'bg-[var(--color-primary-500)] border-[var(--color-primary-500)]'
            : errors.age ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
        }`}>
          {ageConfirmed && (
            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
          {t('paymentCard.ageConfirm', 'I confirm that I am at least 18 years old and of legal age in my jurisdiction.')}
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-2.5 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))`,
          boxShadow: '0 4px 14px -3px color-mix(in srgb, var(--color-primary-500) 40%, transparent)',
        }}
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t('paymentCard.processing', 'Processing...')}</>
        ) : (
          <><Shield className="h-3.5 w-3.5" /> {t('paymentCard.saveCard', 'Save Card')}</>
        )}
      </button>

      <div className="flex items-center justify-center gap-1.5 mt-2">
        <Shield className="h-3 w-3 text-gray-300 dark:text-gray-600" />
        <span className="text-[10px] text-gray-400 dark:text-gray-500">{t('paymentCard.securePayment', 'Secure encrypted payment')}</span>
      </div>
    </form>
  );
}
